import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronDown,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  X,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, cx } from "./ui";
import { QuickAddForm, FollowUpList } from "./QuickAddForm";
import { WhatsAppComposer } from "./WhatsAppComposer";
import { telLink, whatsappLink, whatsappNumber } from "../lib/links";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { formatDateTime, formatElapsed, formatMoney, initials } from "../lib/format";
import type { Lead, LeadBooking } from "../api/types";

type Tab = "followups" | "bookings" | "notes";

/** The kinds that mean somebody actually reached them. */
const CONTACT_KINDS = ["call", "email", "sms", "whatsapp", "meeting"];

/**
 * The lead detail modal.
 *
 * Reached by clicking a lead's name. Everything about one enquiry in one place:
 * who they are, what they asked for, what it is worth, when they might come, and
 * everything that has been said and done.
 *
 * A slide-over rather than a centred dialog, because it is a workspace you keep
 * open beside the queue rather than a task you finish and dismiss.
 */
export function LeadDetailModal({
  leadId,
  onClose,
  onChanged,
}: {
  leadId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const timeZone = useAccountTimeZone();
  const [tab, setTab] = useState<Tab>("followups");
  const [tagDraft, setTagDraft] = useState("");
  const [whatsappOpen, setWhatsappOpen] = useState(false);

  const lead = useQuery({
    queryKey: ["lead", leadId],
    queryFn: ({ signal }) => api.lead(leadId!, signal),
    enabled: Boolean(leadId),
  });

  const activities = useQuery({
    queryKey: ["lead-activities-one", leadId],
    queryFn: ({ signal }) => api.leadActivities(leadId!, signal),
    enabled: Boolean(leadId),
  });

  // The agreed slot's fate, only when the Bookings tab is open. One request,
  // and it answers "did it get held, and why not" for the panel under it.
  const booking = useQuery({
    queryKey: ["lead-booking", leadId],
    queryFn: ({ signal }) => api.leadBooking(leadId!, signal),
    enabled: Boolean(leadId && tab === "bookings"),
  });
  const book = useMutation({
    mutationFn: () => api.bookLead(leadId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead-booking", leadId] });
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    },
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 10 * 60_000,
  });

  // Escape closes, and the body stops scrolling behind the panel.
  useEffect(() => {
    if (!leadId) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [leadId, onClose]);

  useEffect(() => {
    setTab("followups");
    setWhatsappOpen(false);
  }, [leadId]);

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.updateLead>[1]) => api.updateLead(leadId!, body),
    onSuccess: () => {
      onChanged();
      void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    },
  });

  const logActivity = useMutation({
    mutationFn: (body: Parameters<typeof api.logLeadActivity>[1]) =>
      api.logLeadActivity(leadId!, body),
    onSuccess: () => {
      onChanged();
      void queryClient.invalidateQueries({ queryKey: ["lead-activities-one", leadId] });
    },
  });

  if (!leadId) return null;

  const data = lead.data;
  const tags = data?.tags ?? [];
  // Prefer what the lead was captured with; fall back to the contact's, or a
  // lead captured from a contact is uncallable and the actions below vanish.
  const phone = data?.phone || data?.contact_phone || "";
  const email = data?.email || data?.contact_email || "";
  const tel = telLink(phone);
  const waNumber = whatsappNumber(phone);
  const waLink = whatsappLink(phone);

  function addTag(raw: string) {
    const tag = raw.trim().replace(/,$/, "");
    if (!data || !tag) return;
    if (tags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      setTagDraft("");
      return;
    }
    patch.mutate({ tags: [...tags, tag] } as never);
    setTagDraft("");
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        aria-label="Close lead details"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Lead details"
        className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-2xl"
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
          <h2 className="text-sm font-semibold">Lead details</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </header>

        {lead.isPending ? (
          <div className="space-y-3 p-5">
            <div className="h-8 w-40 animate-pulse rounded bg-slate-100" />
            <div className="h-24 animate-pulse rounded bg-slate-100" />
            <div className="h-32 animate-pulse rounded bg-slate-100" />
          </div>
        ) : lead.isError || !data ? (
          <div className="p-5">
            <p className="text-sm text-slate-600">
              {lead.error instanceof ApiError ? lead.error.message : "Could not load this lead."}
            </p>
          </div>
        ) : (
          <div className="space-y-5 p-5">
            {/* Who and how to reach them. */}
            <div className="flex items-start gap-3">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-pink-100 text-sm font-semibold text-pink-700">
                {initials(data.display_name || "?")}
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2">
                  <span className="truncate text-base font-semibold text-slate-900">
                    {data.display_name || "Unnamed enquiry"}
                  </span>
                  <span
                    className={cx(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase ring-1 ring-inset",
                      data.status === "converted"
                        ? "bg-violet-50 text-violet-700 ring-violet-600/20"
                        : data.is_open
                          ? "bg-sky-50 text-sky-700 ring-sky-600/20"
                          : "bg-slate-100 text-slate-500 ring-slate-400/20",
                    )}
                  >
                    {data.status}
                  </span>
                </p>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600">
                  {tel ? (
                    <a href={tel ?? undefined} className="inline-flex items-center gap-1 hover:underline">
                      <Phone size={12} /> {phone}
                    </a>
                  ) : null}
                  {waNumber ? (
                    <a
                      href={waLink ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Message on WhatsApp"
                      className="inline-flex items-center gap-1 hover:underline"
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  ) : null}
                  {email ? (
                    <a href={`mailto:${email}`} className="inline-flex items-center gap-1 hover:underline">
                      <Mail size={12} /> {email}
                    </a>
                  ) : null}
                  {!phone && !email ? <span className="text-slate-400">—</span> : null}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                {tel ? (
                  <a
                    href={tel ?? undefined}
                    aria-label={`Call ${phone}`}
                    title="Call"
                    className="rounded-full p-2 text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <Phone size={18} />
                  </a>
                ) : null}
                {waNumber ? (
                  <button
                    onClick={() => setWhatsappOpen((v) => !v)}
                    aria-expanded={whatsappOpen}
                    aria-label="Message on WhatsApp"
                    title="Message on WhatsApp"
                    className={cx(
                      "rounded-full p-2 ring-1 ring-inset transition",
                      whatsappOpen
                        ? "bg-emerald-600 text-white ring-emerald-600"
                        : "text-emerald-700 ring-emerald-600/30 hover:bg-emerald-50",
                    )}
                  >
                    <MessageCircle size={18} />
                  </button>
                ) : null}
                {email ? (
                  <a
                    href={`mailto:${email}`}
                    aria-label="Email"
                    className="rounded-full p-2 text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <Mail size={18} />
                  </a>
                ) : null}
                {data.contact ? (
                  <Link
                    to={`/customers/contacts/${data.contact}`}
                    aria-label="Open the contact"
                    className="rounded-full p-2 text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <MessageSquare size={18} />
                  </Link>
                ) : null}
              </div>
            </div>

            {whatsappOpen ? (
              <WhatsAppComposer
                phone={phone}
                firstName={data.first_name ?? undefined}
                onClose={() => setWhatsappOpen(false)}
              />
            ) : null}

            {/* The two numbers that decide whether to spend time here. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-amber-50 p-3 ring-1 ring-inset ring-amber-600/20">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-amber-800">
                    {data.is_confirmed ? "Confirmed time" : "Tentative time"}
                  </p>
                  <Link
                    to={`/customers/leads/${data.id}/edit`}
                    className="text-xs text-amber-800 underline"
                  >
                    Edit
                  </Link>
                </div>
                <p className="mt-1 text-sm font-semibold tabular-nums text-amber-900">
                  {data.requested_start_at
                    ? formatDateTime(data.requested_start_at, timeZone)
                    : "None set"}
                </p>
                {data.requested_start_at && !data.is_confirmed ? (
                  <Button
                    variant="primary"
                    className="mt-2 bg-emerald-600 px-2.5 py-1 text-xs hover:bg-emerald-700"
                    onClick={() => api.confirmLead(data.id).then(() => {
                      onChanged();
                      void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
                    })}
                  >
                    <Check size={12} /> Confirm
                  </Button>
                ) : null}
              </div>

              <div className="rounded-lg bg-emerald-50 p-3 ring-1 ring-inset ring-emerald-600/20">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-emerald-800">Lead value</p>
                  <Link
                    to={`/customers/leads/${data.id}/edit`}
                    className="text-xs text-emerald-800 underline"
                  >
                    Edit
                  </Link>
                </div>
                <p className="mt-1 text-xl font-semibold tabular-nums text-emerald-900">
                  {data.quoted_price ? formatMoney(data.quoted_price) : "—"}
                </p>
                {data.product_name ? (
                  <p className="text-xs text-emerald-700">{data.product_name}</p>
                ) : null}
              </div>
            </div>

            {/* Everything editable about the enquiry. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Source">
                <Select
                  value={data.source ?? ""}
                  onChange={(source) => patch.mutate({ source } as never)}
                >
                  <option value="">Not set</option>
                  <option value="walk_in">Walk in</option>
                  <option value="phone">Phone</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="instagram">Instagram</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="google">Google</option>
                  <option value="existing_client">Existing client</option>
                  <option value="other">Other</option>
                </Select>
              </Field>

              <Field label="Status">
                <Select
                  value={data.status ?? ""}
                  disabled={data.next_statuses.length === 0}
                  onChange={(status) => api.setLeadStatus(data.id, status).then(() => {
                    onChanged();
                    void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
                  })}
                >
                  <option value={data.status}>{data.status}</option>
                  {data.next_statuses.map((next) => (
                    <option key={next} value={next}>
                      {next}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Priority">
                <Select
                  value={data.priority ?? "normal"}
                  onChange={(next: string) => {
                    // Stored as a score; the UI works in low/normal/high.
                    const score = next === "high" ? 80 : next === "low" ? 20 : 50;
                    patch.mutate({ score } as never);
                  }}
                >
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </Select>
              </Field>

              <Field label="Owner">
                <Select
                  value={data.assigned_employee ?? ""}
                  onChange={(assigned_employee) => patch.mutate({ assigned_employee } as never)}
                >
                  <option value="">Unassigned</option>
                  {(employees.data ?? []).map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.display_name || employee.employee_code}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Lead created">
                <p className="py-2 text-sm tabular-nums text-slate-700">
                  {formatDateTime(data.created_at, timeZone)}
                </p>
              </Field>

              <Field label="Last contact">
                <p className="py-2 text-sm text-slate-700">
                  {activities.data?.length
                    ? (() => {
                        // `type` is optional on the wire, so it has to be
                        // narrowed before the membership test.
                        const contact = activities.data.find(
                          (a) => a.type && CONTACT_KINDS.includes(a.type),
                        );
                        return contact ? (
                          <>
                            {formatElapsed(contact.created_at)}
                            <span className="text-slate-400">
                              {" · "}
                              {formatDateTime(contact.created_at, timeZone)}
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-400">Notes only</span>
                        );
                      })()
                    : "Never"}
                </p>
              </Field>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                Tags
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                  >
                    {tag}
                    <button
                      onClick={() =>
                        patch.mutate({ tags: tags.filter((t) => t !== tag) } as never)
                      }
                      aria-label={`Remove tag ${tag}`}
                      className="text-slate-400 hover:text-slate-700"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
                <input
                  value={tagDraft}
                  onChange={(event) => setTagDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === ",") {
                      event.preventDefault();
                      addTag(tagDraft);
                    }
                  }}
                  onBlur={() => addTag(tagDraft)}
                  placeholder="+ Add tag"
                  aria-label="Add tag"
                  className="w-28 rounded-full border-0 bg-white px-2 py-0.5 text-xs ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900"
                />
              </div>
            </div>

            <div className="rounded-lg bg-slate-50 p-3">
              <p className="mb-2 text-xs font-semibold text-slate-700">Quick add follow-up</p>
              <QuickAddForm
                onSave={(body) => logActivity.mutateAsync(body)}
                onCancel={() => undefined}
              />
            </div>

            <div>
              <label
                htmlFor="lead-notes"
                className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500"
              >
                Notes
              </label>
              <textarea
                id="lead-notes"
                rows={3}
                defaultValue={data.notes ?? ""}
                onBlur={(event) => {
                  if (event.target.value !== data.notes) {
                    patch.mutate({ notes: event.target.value } as never);
                  }
                }}
                className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900"
              />
            </div>

            {/* Follow-ups, bookings and notes. */}
            <div className="border-t border-slate-100 pt-4">
              <div className="mb-3 flex items-center gap-4 border-b border-slate-100">
                {(
                  [
                    ["followups", "Follow-ups", activities.data?.length ?? 0],
                    ["bookings", "Bookings", null],
                    ["notes", "Notes", tags.length ? null : null],
                  ] as const
                ).map(([value, label, count]) => (
                  <button
                    key={value}
                    onClick={() => setTab(value)}
                    className={cx(
                      "border-b-2 pb-2 text-sm font-medium",
                      tab === value
                        ? "border-slate-900 text-slate-900"
                        : "border-transparent text-slate-500 hover:text-slate-700",
                    )}
                  >
                    {label}
                    {count !== null ? (
                      <span className="ml-1 text-xs text-slate-400">{count}</span>
                    ) : null}
                  </button>
                ))}
              </div>

              {tab === "followups" ? (
                activities.isPending ? (
                  <p className="py-6 text-center text-sm text-slate-400">Loading…</p>
                ) : (
                  <FollowUpList
                    activities={activities.data ?? []}
                    emptyHint="Nothing logged against this enquiry yet."
                  />
                )
              ) : tab === "bookings" ? (
              <BookingTab
                data={data}
                outcome={booking.data}
                busy={book.isPending}
                bookError={book.error instanceof ApiError ? book.error : null}
                onBook={() => book.mutate()}
                timeZone={timeZone}
              />
            ) : (
                <p className="py-6 text-center text-sm text-slate-400">
                  Notes are in the field above. Structured notes arrive with the
                  activity log.
                </p>
              )}
            </div>

          </div>
        )}
      </aside>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-slate-500">{label}</p>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  disabled,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={cx(
          "w-full appearance-none rounded-md border-0 bg-white py-1.5 pl-2.5 pr-7 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50 disabled:text-slate-400",
        )}
      >
        {children}
      </select>
      <ChevronDown
        size={13}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
      />
    </div>
  );
}

/**
 * The Bookings tab: did the agreed slot get held, and why not.
 *
 * Reads ``/leads/{id}/booking``, the answer the server computed when the lead
 * was confirmed or won. Three outcomes, three pieces of copy: the slot is held
 * (shown, with a path to the calendar), the lead cannot be booked yet (the
 * reason is spelled out, e.g. no agreed time), or the agreed time is taken
 * (offered the calendar directly). The "Reserve" action is idempotent, so a
 * fresh hold after a cancelled clash is exactly one tap.
 */
function BookingTab({
  data,
  outcome,
  busy,
  bookError,
  onBook,
  timeZone,
}: {
  data: Lead;
  outcome: LeadBooking | null | undefined;
  busy: boolean;
  bookError: ApiError | null;
  onBook: () => void;
  timeZone: string | undefined;
}) {
  const booking = outcome?.booking;

  if (outcome === undefined) {
    return <p className="py-6 text-center text-sm text-slate-400">Checking the slot…</p>;
  }

  if (booking) {
    return (
      <div className="rounded-lg bg-emerald-50 p-4 ring-1 ring-inset ring-emerald-600/25">
        <p className="text-sm font-semibold text-emerald-900">
          {formatDateTime(booking.starts_at, timeZone)} · {booking.service_name}
        </p>
        <p className="mt-0.5 text-xs text-emerald-700">
          With {booking.employee_name || "the assigned provider"}, for {booking.minutes} minutes.
          The slot is held — double-booking is impossible.
        </p>
        <Link
          to="/scheduling/calendar"
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-900 underline"
        >
          <MessageSquare size={12} /> See it on the calendar
        </Link>
      </div>
    );
  }

  const reason = outcome?.blocked_reason ?? "The agreed time could not be held.";
  const bookable = Boolean(data.requested_start_at && data.contact && data.assigned_employee);

  return (
    <div className="rounded-lg bg-amber-50 p-4 ring-1 ring-inset ring-amber-600/25">
      <p className="text-sm font-medium text-amber-900">No booking held yet</p>
      <p className="mt-0.5 text-xs leading-relaxed text-amber-800">{reason}</p>
      {bookable ? (
        <div className="mt-2 flex items-center gap-1.5">
          <Button variant="primary" disabled={busy} onClick={onBook} className="bg-emerald-700 px-2.5 py-1 text-xs hover:bg-emerald-800">
            <Check size={12} /> {busy ? "Reserving…" : "Reserve the agreed slot"}
          </Button>
          <Link to="/scheduling/calendar" className="text-xs font-medium text-amber-900 underline">
            Or pick another time on the calendar
          </Link>
        </div>
      ) : (
        <p className="mt-1 text-[11px] text-amber-800">
          Set the time and a provider on the lead, and this button lights up.
        </p>
      )}
      {bookError ? (
        <p role="alert" className="mt-1 text-xs text-rose-700">
          {bookError.code === "booking_slot_conflict"
            ? "That time is taken. Pick another slot on the calendar."
            : bookError.message}
        </p>
      ) : null}
    </div>
  );
}